import { Link } from "@tanstack/react-router";
import { Play } from "lucide-react";
import type { VideoCardDisplay } from "@/components/video/types";
import { cn } from "@/lib/utils";

export type VideoCardProps = VideoCardDisplay & {
  onClick?: () => void;
  className?: string;
};

const cardClassName =
  "group w-full overflow-hidden rounded-xl border border-border text-left transition-all hover:border-accent hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

function CardBody({
  title,
  excerpt,
  thumbnailHue = 145,
  thumbnailUrl,
  badge = "Blog",
}: Pick<VideoCardProps, "title" | "excerpt" | "thumbnailHue" | "thumbnailUrl" | "badge">) {
  return (
    <>
      <div
        className="relative flex aspect-video items-center justify-center bg-cover bg-center"
        style={
          thumbnailUrl
            ? { backgroundImage: `url(${thumbnailUrl})` }
            : {
                background: `linear-gradient(145deg, hsl(${thumbnailHue} 40% 28%), hsl(${thumbnailHue} 50% 16%))`,
              }
        }
      >
        {badge ? (
          <span className="absolute left-3 top-3 rounded-md bg-black/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/90">
            {badge}
          </span>
        ) : null}
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white transition-transform group-hover:scale-105">
          <Play className="h-5 w-5 fill-white/90 text-white/90" />
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-display text-base font-semibold leading-snug">{title}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{excerpt}</p>
      </div>
    </>
  );
}

export function VideoCard({
  title,
  excerpt,
  thumbnailHue = 145,
  thumbnailUrl,
  badge = "Blog",
  to,
  params,
  onClick,
  className,
}: VideoCardProps) {
  const body = (
    <CardBody
      title={title}
      excerpt={excerpt}
      thumbnailHue={thumbnailHue}
      thumbnailUrl={thumbnailUrl}
      badge={badge}
    />
  );

  if (to && params) {
    return (
      <Link to={to} params={params} className={cn(cardClassName, "block", className)}>
        {body}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={cn(cardClassName, className)}>
      {body}
    </button>
  );
}
